import { ruleFields, getTemperatureUnitSuffix, getDifferentialPressureUnitSuffix, getDifferentialPressureDecimals, isCelsiusField, parseDuration } from 'services/utils';
import { isPetGlycolFilterAvailable } from 'services/charting/constants';

export default class ThresholdBuilderController {

    /* @ngInject */
    constructor($scope, FeatureFlags, UserPreferencesManager) {
        this.$scope = $scope;
        this.FeatureFlags = FeatureFlags;
        this.UserPreferencesManager = UserPreferencesManager;
    }

    $onInit() {
        this.fields = { ...ruleFields };
        if (!this.FeatureFlags.isActive('sensor_virtual_glycol_buffer')) {
            delete this.fields.virtualGlycolBuffer;
        }
        if (!this.FeatureFlags.isActive('notifications_battery_status')) {
            delete this.fields.batteryStatus;
        }
        this.triggerMode = 'immediately';
        this.minTriggerDelay = 1;
        this.showAdvancedOptions = false;
        this.glycolFilterEnabled = false;
        this.triggerDelayHours = 0;
        this.triggerDelayMinutes = 0;
        this.acknowledgmentDuration = { hours: 4, minutes: 0 };
        this.invalidAcknowledgmentDuration = false;

        this.initFromBindings();
    }

    // `trigger` gets a new reference each time the parent loads a different template.
    // Without this, local state from $onInit persists and shows stale values.
    $onChanges(changes) {
        if (changes.trigger && !changes.trigger.isFirstChange()) {
            this.triggerMode = 'immediately';
            this.minTriggerDelay = 1;
            this.showAdvancedOptions = false;
            this.glycolFilterEnabled = false;
            this.triggerDelayHours = 0;
            this.triggerDelayMinutes = 0;
            this.acknowledgmentDuration = { hours: 4, minutes: 0 };
            this.invalidAcknowledgmentDuration = false;
            this.initFromBindings();
        }
    }

    initFromBindings() {
        if (this.triggerDelay) {
            this.triggerMode = 'delay';
            const d = parseDuration(this.triggerDelay);
            this.triggerDelayHours = d.hours() + (d.days() * 24);
            this.triggerDelayMinutes = d.minutes();
        }

        if (this.trigger?.field === 'connectionStatus' &&
            this.trigger?.connection === 'SENSOR_OFFLINE') {
            this.minTriggerDelay = 15;
            this.triggerMode = 'delay';
        }

        if (this.trigger?.range?.filter?.productEquivalentTemperature !== undefined) {
            this.glycolFilterEnabled = true;
        }
        this.hasSavedPetGlycolFilter = this.glycolFilterEnabled;

        if (this.unacknowledgesAfter) {
            const d = parseDuration(this.unacknowledgesAfter);
            this.acknowledgmentDuration = {
                hours: d.hours() + (d.days() * 24),
                minutes: d.minutes(),
            };
        }

        if (this.reminderNotifications || this.resolvedNotifications || this.glycolFilterEnabled || this.unacknowledgesAfter) {
            this.showAdvancedOptions = true;
        }
    }

    // --- Trigger field ---

    triggerFieldChanged() {
        const field = this.trigger.field;
        // Reset trigger to just the field
        Object.keys(this.trigger).forEach(key => {
            if (key !== 'field') delete this.trigger[key];
        });

        const operator = this.getOperatorByField(field);
        if (operator === 'range') {
            this.trigger.range = { type: 'ABOVE', lower: null, upper: null };
        } else if (operator === 'count') {
            this.trigger.triggerCount = 2;
        }

        if (this.fields[field]?.default) {
            Object.assign(this.trigger, this.fields[field].default);
        }

        if (field === 'connectionStatus') {
            this.connectionStatusTypeChanged();
        }

        this.showAdvancedOptions = false;
        this.glycolFilterEnabled = false;
        this.triggerMode = 'immediately';
        this.onDelayChange({ $event: null });
        this.notifyNotificationsChange(false, false);

        if (this.onFieldChange) {
            this.onFieldChange({ $event: field });
        }
    }

    connectionStatusTypeChanged() {
        if (this.trigger.connection === 'SENSOR_OFFLINE') {
            this.triggerMode = 'delay';
            const currentDelay = this.triggerDelay ? parseInt(this.triggerDelay, 10) : 0;
            if (currentDelay < 15 * 60) {
                this.onDelayChange({ $event: `${15 * 60}s` });
            }
            this.minTriggerDelay = 15;
        } else {
            this.minTriggerDelay = 1;
            this.triggerMode = this.triggerDelay ? 'delay' : 'immediately';
        }
    }

    getDisplayOption(value) {
        return this.fields[this.trigger.field]?.operatorDisplayNames?.[value] || value;
    }

    getOperatorByField(field) {
        return this.fields[field]?.operator;
    }

    getUnitByField(field) {
        if (isCelsiusField(field)) {
            return getTemperatureUnitSuffix();
        }
        if (field === 'differentialPressure') {
            return getDifferentialPressureUnitSuffix();
        }
        return this.fields[field]?.unit || '';
    }

    getStepByField(field) {
        if (field === 'differentialPressure') {
            return 10 ** -getDifferentialPressureDecimals();
        }
        return 0.01;
    }

    // Still shown for triggers saved with it, so the setting stays visible and can be turned off
    showPetGlycolFilter() {
        return isPetGlycolFilterAvailable(this.FeatureFlags) || this.hasSavedPetGlycolFilter;
    }

    isPetGlycolFilterReplaced() {
        return !isPetGlycolFilterAvailable(this.FeatureFlags);
    }

    triggerRangeTypeChanged() {
        if (this.trigger.range.type === 'ABOVE') {
            this.trigger.range.lower = null;
        } else if (this.trigger.range.type === 'BELOW') {
            this.trigger.range.upper = null;
        }
    }

    // --- Trigger delay ---

    triggerModeChanged() {
        if (this.triggerMode === 'immediately') {
            this.onDelayChange({ $event: null });
        } else {
            this.onDelayChange({ $event: `${this.minTriggerDelay * 30 * 60}s` });
        }
    }

    triggerDelayChanged() {
        const totalSeconds = (this.triggerDelayHours * 3600) + (this.triggerDelayMinutes * 60);
        if (totalSeconds > 0) {
            this.onDelayChange({ $event: `${totalSeconds}s` });
        }
    }

    // --- Advanced options ---

    glycolFilterChanged() {
        if (this.glycolFilterEnabled) {
            if (!this.trigger.range) {
                this.trigger.range = {};
            }
            this.trigger.range.filter = { productEquivalentTemperature: {} };
        } else if (this.trigger.range) {
            this.trigger.range.filter = null;
        }
    }

    acknowledgmentDurationChanged() {
        const totalSeconds = (this.acknowledgmentDuration.hours * 3600) + (this.acknowledgmentDuration.minutes * 60);
        this.invalidAcknowledgmentDuration = totalSeconds < 60 || totalSeconds > 604800;
        if (!this.invalidAcknowledgmentDuration) {
            this.onAckChange({ $event: `${totalSeconds}s` });
        }
    }

    notifyNotificationsChange(reminder, resolved) {
        this.onNotificationsChange({
            $event: { reminderNotifications: reminder, resolvedNotifications: resolved }
        });
    }

    onReminderChange() {
        this.notifyNotificationsChange(this.reminderNotifications, this.resolvedNotifications);
    }

    onResolvedChange() {
        this.notifyNotificationsChange(this.reminderNotifications, this.resolvedNotifications);
    }
}
